import React, { useContext, useState } from "react";
import "./SigninSignup.css";
import { useNavigate } from "react-router-dom";
import { translations } from "../../utils/lang";
import { Context } from "../../context/Context";
import { homeRouteFor } from "../../access/core/routePolicies";
import config from "../../config.json";
import { Button, Input, Card, NoticeBox } from "../../components/ui";
import Logo from "../../assets/logo/logo.svg";
import { errorText, normalizeThrown } from "../../utils/errors/normalizeApiError";

const SIGNIN = "sign in";
const SIGNUP = "sign up";
const FORGOT_PASSWORD = "forgot password";

const RESET_REQUEST = "request";
const RESET_VERIFY = "verify";
const RESET_PASSWORD = "reset";

export const SigninSignup = () => {
  const ALLOWED_EMAILS = config.ALLOWED_EMAILS;
  const BASE_AUTH_API = config.BASE_AUTH_API;
  const { lang, setIsAuthenticated, setCurrentUserInfo } = useContext(Context);

  const [state, setState] = useState(SIGNIN);
  const [resetStep, setResetStep] = useState(RESET_REQUEST);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    recoveryEmail: "",
    resetCode: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const forgotPasswordText = {
    ar: {
      title: "نسيت كلمة المرور",
      requestDescription:
        "ادخل بريدك الإلكتروني لإرسال كود التحقق",
      verifyDescription: "ادخل كود التحقق المكوّن من 4 حروف",
      resetDescription: "ادخل كلمة المرور الجديدة وتأكيد كلمة المرور",
      link: "نسيت كلمة المرور؟",
      back: "العودة لتسجيل الدخول",
      recoveryEmail: "بريدك الإلكتروني ",
      code: "كود التحقق",
      newPassword: "كلمة المرور الجديدة",
      confirmPassword: "تأكيد كلمة المرور",
      send: "إرسال الكود",
      verify: "تحقق من الكود",
      reset: "تحديث كلمة المرور",
      codeSent: "تم إرسال كود التحقق إلى بريدك الإلكتروني المسجل",
      codeVerified: "تم التحقق من الكود بنجاح",
      passwordUpdated: "تم تحديث كلمة المرور بنجاح، يمكنك تسجيل الدخول الآن",
      recoveryEmailRequired: "بريدك الإلكتروني مطلوب",
      recoveryEmailInvalid: "أدخل بريدًا إلكترونيًا صالحًا",
      recoveryEmailHint: "أدخل البريد الذي سُجّل لحسابك. إن لم يكن لحسابك بريد مسجّل فتواصل مع المسؤول أو الدعم.",
      codeRequired: "كود التحقق يجب أن يتكوّن من 4 حروف",
      passwordRequired: "كلمة المرور يجب ألا تقل عن 6 أحرف",
      passwordMismatch: "كلمتا المرور غير متطابقتين",
      failed: "تعذر إكمال طلب إعادة تعيين كلمة المرور",
    },
    en: {
      title: "Forgot Password",
      requestDescription:
        "Enter your email address to send a verification code to your registered email",
      verifyDescription: "Enter the 4-letter verification code",
      resetDescription: "Enter and confirm your new password",
      link: "Forgot password?",
      back: "Back to sign in",
      recoveryEmail: "Email Address",
      code: "Verification code",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      send: "Send code",
      verify: "Verify code",
      reset: "Update password",
      codeSent: "A verification code was sent to your registered email",
      codeVerified: "The verification code was confirmed successfully",
      passwordUpdated: "Password updated successfully. You can now sign in",
      recoveryEmailRequired: "Email address is required",
      recoveryEmailInvalid: "Enter a valid email address",
      recoveryEmailHint: "Enter the email registered for your account. If your account has no email on file, contact your administrator or support.",
      codeRequired: "The verification code must contain 4 letters",
      passwordRequired: "Password must contain at least 6 characters",
      passwordMismatch: "Passwords do not match",
      failed: "Unable to complete the password reset request",
    },
  };

  const fpText = forgotPasswordText[lang] || forgotPasswordText.ar;

  const resetForgotPasswordState = () => {
    setResetStep(RESET_REQUEST);
    setFormData((prev) => ({
      ...prev,
      recoveryEmail: "",
      resetCode: "",
      newPassword: "",
      confirmPassword: "",
    }));
    setErrors({});
    setSuccessMessage("");
  };

  const changeHandler = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === "resetCode"
        ? value.replace(/[^a-zA-Z]/g, "").slice(0, 4).toUpperCase()
        : value;

    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "", allow: "" }));
    setSuccessMessage("");
  };

  const validate = () => {
    let valid = true;
    const newErrors = {};

    if (state === FORGOT_PASSWORD) {
      if (!formData.recoveryEmail.trim()) {
        newErrors.recoveryEmail = fpText.recoveryEmailRequired;
        valid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.recoveryEmail.trim())) {
        newErrors.recoveryEmail = fpText.recoveryEmailInvalid;
        valid = false;
      }

      if (
        resetStep !== RESET_REQUEST &&
        !/^[A-Z]{4}$/.test(formData.resetCode.trim().toUpperCase())
      ) {
        newErrors.resetCode = fpText.codeRequired;
        valid = false;
      }

      if (resetStep === RESET_PASSWORD) {
        if (!formData.newPassword || formData.newPassword.length < 6) {
          newErrors.newPassword = fpText.passwordRequired;
          valid = false;
        }

        if (formData.newPassword !== formData.confirmPassword) {
          newErrors.confirmPassword = fpText.passwordMismatch;
          valid = false;
        }
      }

      setErrors(newErrors);
      return valid;
    }

    if (state === SIGNUP && !formData.username.trim()) {
      newErrors.username = translations[lang].signinup.nameRequired;
      valid = false;
    }

    if (formData.email.trim()) {
      if (state === SIGNUP && !ALLOWED_EMAILS.includes(formData.email)) {
        newErrors.allow = translations[lang].signinup.notAllowd;
        valid = false;
      }
    }

    if (
      !formData.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = translations[lang].signinup.emailRequired;
      valid = false;
    }

    if (!formData.password.trim() || formData.password.length < 6) {
      newErrors.password = translations[lang].signinup.passwordRequired;
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const requestResetCode = async () => {
    const response = await fetch(`${BASE_AUTH_API}forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: formData.recoveryEmail.trim().toLocaleLowerCase() }),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || fpText.failed);
    }

    setResetStep(RESET_VERIFY);
    setSuccessMessage(fpText.codeSent);
  };

  const verifyResetCode = async () => {
    const response = await fetch(`${BASE_AUTH_API}verify-reset-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: formData.recoveryEmail.trim().toLocaleLowerCase(),
        code: formData.resetCode.trim().toUpperCase(),
      }),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || fpText.failed);
    }

    setResetStep(RESET_PASSWORD);
    setSuccessMessage(fpText.codeVerified);
  };

  const resetPassword = async () => {
    const response = await fetch(`${BASE_AUTH_API}reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: formData.recoveryEmail.trim().toLocaleLowerCase(),
        code: formData.resetCode.trim().toUpperCase(),
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      }),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || fpText.failed);
    }

    setState(SIGNIN);
    setResetStep(RESET_REQUEST);
    setFormData((prev) => ({
      ...prev,
      password: "",
      recoveryEmail: "",
      resetCode: "",
      newPassword: "",
      confirmPassword: "",
    }));
    setErrors({});
    setSuccessMessage(fpText.passwordUpdated);
  };

  const forgotPassword = async () => {
    if (!validate()) return;

    setErrors({});
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      if (resetStep === RESET_REQUEST) {
        await requestResetCode();
      } else if (resetStep === RESET_VERIFY) {
        await verifyResetCode();
      } else {
        await resetPassword();
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, allow: error.message || fpText.failed }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const signin = async () => {
    if (!validate()) return;

    let validData = { ...formData, email: formData.email.toLowerCase() };
    let responseData;

    setIsSubmitting(true);
    try {
      await fetch(`${BASE_AUTH_API}signin`, {
        method: "POST",
        headers: {
          Accept: "application/form-data",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(validData),
      })
        .then((response) => response.json())
        .then((data) => (responseData = data));

      if (responseData.success) {
        localStorage.setItem("auth-token", responseData.token);
        setIsAuthenticated(responseData.token);

        const localUser = {
          role: responseData.role,
          between: responseData.between,
          name: responseData.name,
          assignedBranch: responseData.assignedBranch,
          _id: responseData._id,
        };

        setCurrentUserInfo(localUser);

        navigate(homeRouteFor(localUser) || "/", { replace: true });
      } else {
        if (responseData.errors === "Email not found") {
          setErrors((prev) => ({
            ...prev,
            email: translations[lang].signinup.emailNotFound,
          }));
        } else if (responseData.errors === "Incorrect password") {
          setErrors((prev) => ({
            ...prev,
            password: translations[lang].signinup.passwordIncorrect,
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            allow: translations[lang].signinup.notAllowd,
          }));
        }
      }
    } catch (error) {
      // No answer (network, server down, unreadable body): say so inline, once; it is not a refusal of the account.
      setErrors((prev) => ({
        ...prev,
        allow: errorText(normalizeThrown(error), lang),
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const signup = async () => {
    if (!validate()) return;

  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (state === FORGOT_PASSWORD) {
      forgotPassword();
    } else if (state === SIGNUP) {
      signup();
    } else {
      signin();
    }
  };

  const forgotDescription =
    resetStep === RESET_REQUEST
      ? fpText.requestDescription
      : resetStep === RESET_VERIFY
        ? fpText.verifyDescription
        : fpText.resetDescription;

  const forgotButtonText =
    resetStep === RESET_REQUEST
      ? fpText.send
      : resetStep === RESET_VERIFY
        ? fpText.verify
        : fpText.reset;

  return (
    <div className={`signinsignup ${lang === "ar" ? "ar" : "en"}`}>
      <Card className="signinsignup-container" hoverable={false}>
        <form className="signinsignup-form" onSubmit={handleSubmit}>
          <img src={Logo} alt="LUD" className="signinsignup-logo" />

          <h1>
            {state === FORGOT_PASSWORD
              ? fpText.title
              : state === SIGNIN
                ? translations[lang].signinup.signin
                : translations[lang].signinup.signup}
          </h1>

          {state === FORGOT_PASSWORD && (
            <p className="signinsignup-forgot-description">
              {forgotDescription}
            </p>
          )}

          <div className="signinsignup-fields">
            {state === SIGNUP && (
              <Input
                type="text"
                label={translations[lang].signinup.name}
                placeholder={translations[lang].signinup.name}
                name="username"
                autoComplete="name"
                value={formData.username}
                onChange={changeHandler}
                error={errors.username}
                required
                fullWidth
              />
            )}


            {state === FORGOT_PASSWORD ? (
              <>
                <Input
                  type="email"
                  label={fpText.recoveryEmail}
                  placeholder={fpText.recoveryEmail}
                  name="recoveryEmail"
                  autoComplete="email"
                  value={formData.recoveryEmail}
                  onChange={changeHandler}
                  error={errors.recoveryEmail}
                  helperText={resetStep === RESET_REQUEST ? fpText.recoveryEmailHint : undefined}
                  disabled={resetStep !== RESET_REQUEST}
                  required
                  fullWidth
                />

                {resetStep !== RESET_REQUEST && (
                  <Input
                    type="text"
                    label={fpText.code}
                    placeholder={fpText.code}
                    name="resetCode"
                    autoComplete="one-time-code"
                    value={formData.resetCode}
                    onChange={changeHandler}
                    error={errors.resetCode}
                    disabled={resetStep === RESET_PASSWORD}
                    required
                    fullWidth
                  />
                )}

                {resetStep === RESET_PASSWORD && (
                  <>
                    <Input
                      type="password"
                      label={fpText.newPassword}
                      placeholder={fpText.newPassword}
                      name="newPassword"
                      autoComplete="new-password"
                      value={formData.newPassword}
                      onChange={changeHandler}
                      error={errors.newPassword}
                      required
                      fullWidth
                    />
                    <Input
                      type="password"
                      label={fpText.confirmPassword}
                      placeholder={fpText.confirmPassword}
                      name="confirmPassword"
                      autoComplete="new-password"
                      value={formData.confirmPassword}
                      onChange={changeHandler}
                      error={errors.confirmPassword}
                      required
                      fullWidth
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <Input
                  autoComplete="email"
                  type="email"
                  label={translations[lang].signinup.email}
                  placeholder={translations[lang].signinup.email}
                  name="email"
                  value={formData.email}
                  onChange={changeHandler}
                  error={errors.email}
                  required
                  fullWidth
                />

                <Input
                  type="password"
                  autoComplete="current-password"
                  label={translations[lang].signinup.pass}
                  placeholder={translations[lang].signinup.pass}
                  name="password"
                  value={formData.password}
                  onChange={changeHandler}
                  error={errors.password}
                  required
                  fullWidth
                />

                <div className="signinsignup-links-row">
                  <Button
                    type="button"
                    appearance="text"
                    variant="primary"
                    size="sm"
                    className="signinsignup-link"
                    onClick={() => {
                      setState(FORGOT_PASSWORD);
                      resetForgotPasswordState();
                    }}
                  >
                    {fpText.link}
                  </Button>
                </div>
              </>
            )}
          </div>

          {errors.allow && (
            <NoticeBox
              variant="danger"
              size="sm"
              className="signinsignup-notice"
            >
              {errors.allow}
            </NoticeBox>
          )}

          {successMessage && (
            <NoticeBox
              variant="success"
              size="sm"
              className="signinsignup-notice"
            >
              {successMessage}
            </NoticeBox>
          )}

          <Button
            type="submit"
            variant="primary"
            appearance="filled"
            size="lg"
            fullWidth
            loading={isSubmitting}
            className="signinsignup-submit"
          >
            {state === FORGOT_PASSWORD
              ? forgotButtonText
              : translations[lang].signinup.continue_btn}
          </Button>

          {state === FORGOT_PASSWORD && (
            <div className="signinsignup-switch">
              <Button
                type="button"
                appearance="text"
                variant="primary"
                size="sm"
                className="signinsignup-link"
                onClick={() => {
                  setState(SIGNIN);
                  resetForgotPasswordState();
                }}
              >
                {fpText.back}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};
